import React, { useEffect, useMemo, useState } from "react";
import TranslationWrapper, { getTranslation } from "../../../components/TranslationWrapper/TranslationWrapper";
import { CircularProgress, Collapse, FormLabel, GridLegacy, Grow } from "@mui/material";
import Card from "../../../components/Card/Card";
import CardBody from "../../../components/Card/CardBody";
import { convertArrayToSelectOptions } from "../../../templateHelpers";
import { appPrintMode, printerModes } from "../../../variables/app";
import FormSwitchTC from "../../../components/Forms/FormSwitchTC";
import FormAsyncTC from "../../../components/Forms/FormAsyncTC";
import FormTextTC from "../../../components/Forms/FormTextTC";
import { useController, useFormContext, useWatch } from "react-hook-form";
import FormControlledInputTC from "../../../components/Forms/FormControlledInputTC";

import axios from "axios";
import { VestigingFormContext } from "../VestigingForm";
import FormSelectTC from "../../../components/Forms/FormSelectTC";
import FormFieldTwoColumns from "../../../components/Forms/Layouts/FormFieldTwoColumns.tsx";

function useFetchHasCloudPrinter(url: string, printerId: null | number, kioskPrinterId: null | number) {
  const [response, setResponse] = useState<{ data: boolean } | null>(null);
  const [error, setError] = useState("");
  const [loading, setloading] = useState(false);

  const fetchData = async () => {
    try {
      setloading(true);
      const res = await axios.post("core/hasCloudPrinters", {
        printer_ids: [printerId, kioskPrinterId],
      });
      setResponse(res.data);
    } catch (err) {
      setError(err as string);
    } finally {
      setloading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [printerId, kioskPrinterId]);

  return { response, error, loading };
}

function Printers() {
  const printerModesOptions = useMemo(() => convertArrayToSelectOptions(printerModes), []);
  const appPrintModeOptions = useMemo(() => convertArrayToSelectOptions(appPrintMode), []);
  const { vestigingId, trigger }: VestigingFormContext = useFormContext();
  const sharedCounter = useWatch({ name: "sharedCounter" });
  const defaultPrinterId = useWatch({ name: "defaultPrinter" });
  const kioskPrinterId = useWatch({ name: "kioskPrinter" });
  const printer_state_check = useWatch({ name: "extra_settings__use_printer_state_check" });
  const { loading, error, response } = useFetchHasCloudPrinter(
    `core/hasCloudPrinter`,
    defaultPrinterId,
    kioskPrinterId
  );
  const hasCloudPrinter = response?.data ?? null;
  const atLeastOnePrinterConfigured = defaultPrinterId || kioskPrinterId;
  const {
    field: { value: customData },
  } = useController({ name: "custom_data" });

  useEffect(() => {
    trigger();
  }, [hasCloudPrinter]);

  // @ts-ignore
  return (
    <div style={{ height: "100%", width: "100%", overflow: "hidden" }}>
      <GridLegacy container>
        <GridLegacy item sx={{ width: "100%", mb: 2 }}>
          <Card>
            <CardBody>
              <GridLegacy item>
                <h3>{getTranslation("vestigingen.form.printers.headers.service", "Service settings")}</h3>
              </GridLegacy>
              <FormTextTC
                name={"custom_data.print.service_secret"}
                tDefault={"Service secret"}
                tKey={"vestigingen.form.printers.service_secret"}
              />
              <FormTextTC
                defaultValue={"https://jamezz.app"}
                name={"custom_data.print.service_endpoint"}
                tDefault={"Service endpoint"}
                tKey={"vestigingen.form.printers.service_endpoint"}
              />
              <FormTextTC
                defaultValue={"latest"}
                name={"custom_data.print.service_version"}
                tDefault={"Service version"}
                tKey={"vestigingen.form.algemeen.label.service_version"}
              />
              <FormSwitchTC defaultValue={false} name={"custom_data.print.force_update"} tKey={"Force update"} />

              <FormTextTC
                name={"custom_data.print.service_print_token"}
                tDefault={"Print token"}
                tKey={"vestigingen.form.printers.printToken"}
              />
              <FormTextTC
                name={"apiaccounts__paymentsToken"}
                tDefault={"Payments token"}
                tKey={"vestigingen.form.primairbetallingsverkeer.label.paymentsToken"}
              />
              <FormSwitchTC
                defaultValue={false}
                name={"custom_data.print.service_payments_enabled"}
                tKey={"Payments active"}
              />
              <FormSwitchTC
                defaultValue={true}
                name={"custom_data.print.service_logging_enabled"}
                tKey={"Service log enabled"}
              />
              <FormTextTC
                defaultValue={"1"}
                name={"custom_data.print.service_kitchenscreen_id"}
                tDefault={"Kitchen screen id"}
                tKey={"vestigingen.form.printers.service_kitchenscreen_id"}
              />
            </CardBody>
          </Card>
        </GridLegacy>
        <GridLegacy item sx={{ width: "100%", mb: 2 }}>
          <Card>
            <CardBody>
              <GridLegacy item>
                <h3>{getTranslation("vestigingen.form.printers.headers.printersettings", "Printer settings")}</h3>
              </GridLegacy>
              <FormSwitchTC
                name={"autoPrint"}
                tDefault={"Print bon automatisch"}
                tKey={"vestigingen.form.algemeen.label.autoPrint"}
              />
              <FormSwitchTC
                name={"askForReceipt"}
                tDefault={"Ask to print receipt"}
                tKey={"vestigingen.form.algemeen.label.askForReceipt"}
              />
              <FormSwitchTC
                name={"printRouter"}
                tDefault={"Printer route"}
                tKey={"vestigingen.form.overige.label.printRouter"}
              />{" "}
              <FormSwitchTC
                name={"printToApiLog"}
                tDefault={"Extensive print logging"}
                tKey={"vestigingen.printToApiLog"}
              />
              <FormAsyncTC
                async={{
                  url: "core/printers/search?fields=id as value, name as label&per_page=50",
                  searchField: "name",
                }}
                key={"defaultPrinter" + vestigingId}
                name={"defaultPrinter"}
                nullable={true}
                tDefault={"Standaard printer"}
                tKey={"vestigingen.form.printers.label.defaultPrinter"}
              />
              <FormAsyncTC
                async={{
                  url: "core/printers/search?fields=id as value, name as label&per_page=50",
                  searchField: "name",
                }}
                key={"kioskPrinter" + vestigingId}
                name={"kioskPrinter"}
                nullable={true}
                tDefault={"Kiosk printer"}
                tKey={"vestigingen.form.printers.label.kioskPrinter"}
              />
            </CardBody>
          </Card>
        </GridLegacy>
        <Grow
          in={!hasCloudPrinter || printer_state_check || loading ? atLeastOnePrinterConfigured : null}
          mountOnEnter
          unmountOnExit
        >
          <GridLegacy item sx={{ width: "100%", mb: 2 }}>
            <Card>
              <CardBody>
                <GridLegacy item>
                  <h3>
                    {getTranslation("vestigingen.form.printers.headers.printerstatechecker", "Advanced settings")}
                  </h3>
                </GridLegacy>
                {loading ? (
                  <CircularProgress />
                ) : (
                  <FormSwitchTC
                    key={"extra_settings__use_printer_state_check" + hasCloudPrinter}
                    name={"extra_settings__use_printer_state_check"}
                    rules={{
                      validate: (currentValue) => {
                        return hasCloudPrinter && currentValue
                          ? getTranslation(
                              "vestigingen.form.printers.printer_state_check",
                              "Printer state checker cannot be on when you're using a cloud printer"
                            )
                          : true;
                      },
                    }}
                    tDefault={"Use printer state checker in kiosk (ONLY Jamezz Printing Services)"}
                    tKey={"vestigingen.form.overige.label.use_printer_state_check"}
                  />
                )}
                <FormSelectTC
                  name={"printMode"}
                  options={appPrintModeOptions}
                  tDefault={"Print service service mode"}
                  tKey={"salesarea.appPrintMode"}
                />
                <FormControlledInputTC
                  fieldProps={{
                    inputProps: { min: 1 },
                  }}
                  name={"pollInterval"}
                  rules={{
                    min: {
                      value: 1,
                      message: getTranslation("vestigingen.pollMessage", "The value must not be negative!"),
                    },
                  }}
                  tDefault={"Polling interval"}
                  tKey={"vestigingen.pollInterval"}
                  type={"number"}
                />
              </CardBody>
            </Card>
          </GridLegacy>
        </Grow>
        <GridLegacy item sx={{ width: "100%", mb: 2 }}>
          <Card>
            <CardBody>
              <GridLegacy item>
                <h3>
                  {getTranslation(
                    "vestigingen.form.printers.headers.printerstatechecker",
                    "Customize order pickup codes"
                  )}
                </h3>
              </GridLegacy>
              <FormAsyncTC
                async={{
                  url: "core/vestigingen/search?fields=id as value, systeemNaam as label&per_page=50&sort_field=systeemNaam",
                  searchField: "systeemNaam",
                }}
                name={"sharedCounter"}
                nullable={true}
                tDefault={"Use counters from"}
                tKey={"vestigingen.form.printers.label.useSharedCounter"}
              />
              <Collapse in={!sharedCounter}>
                <FormTextTC
                  name={"pickup_codes__prefix"}
                  tDefault={"Prefix"}
                  tKey={"vestigingen.form.printers.label.prefix"}
                />
                <FormTextTC
                  name={"pickup_codes__postfix"}
                  tDefault={"Postfix"}
                  tKey={"vestigingen.form.printers.label.postfix"}
                />
                <FormControlledInputTC
                  name={"pickup_codes__range_start"}
                  nullable
                  rules={{
                    min: {
                      value: 1,
                      message: getTranslation("The value must not be negative!"),
                    },
                  }}
                  tDefault={"Start value"}
                  tKey={"vestigingen.form.printers.label.range_start"}
                  type={"number"}
                />
                <FormControlledInputTC
                  name={"pickup_codes__range_end"}
                  nullable
                  rules={{
                    max: {
                      value: 999999,
                      message: getTranslation("The value must not be negative!"),
                    },
                  }}
                  tDefault={"End value"}
                  tKey={"vestigingen.form.printers.label.range_end"}
                  type={"number"}
                />
                <FormControlledInputTC
                  name={"pickup_codes__current_value"}
                  nullable
                  rules={{
                    min: {
                      value: 0,
                      message: getTranslation("The value must not be negative!"),
                    },
                    max: {
                      value: 999999,
                      message: getTranslation("The value must not be negative!"),
                    },
                  }}
                  tDefault={"Current value"}
                  tKey={"vestigingen.form.printers.label.current_value"}
                  type={"number"}
                />
              </Collapse>
            </CardBody>
          </Card>
        </GridLegacy>
        <GridLegacy item sx={{ width: "100%", mb: 2 }}>
          <Card>
            <CardBody>
              <GridLegacy item>
                <h3>{getTranslation("vestigingen.form.printers.headers.printinformation", "Output settings")}</h3>
              </GridLegacy>
              <GridLegacy item>
                <FormSwitchTC
                  name={"printVatSpecs"}
                  tDefault={"printVatSpecs"}
                  tKey={"vestigingen.form.algemeen.label.printVatSpecs"}
                />
                <FormSwitchTC
                  name={"printCompanyData"}
                  tDefault={"printCompanyData"}
                  tKey={"vestigingen.form.algemeen.label.printCompanyData"}
                />
                <FormTextTC
                  name={"customPrintHeader"}
                  rows={5}
                  tDefault={"Custom header (removes default header)"}
                  tKey={"vestigingen.printers.headerLines"}
                />
                <FormTextTC
                  name={"extraLine"}
                  rows={5}
                  tDefault={"Custom footer"}
                  tKey={"vestigingen.printers.extraLines"}
                />
                <FormSwitchTC
                  name={"postPrinterUseBuzzer"}
                  tDefault={"Buzzer (print geluid)"}
                  tKey={"vestigingen.form.algemeen.label.postPrinterUseBuzzer"}
                />
                <FormSwitchTC
                  defaultValue={true}
                  name={"custom_data.print.print_pickup_code"}
                  tDefault={"Print pickup code"}
                  tKey={"vestigingen.form.algemeen.label.printPickupCode"}
                />
                <FormTextTC
                  defaultValue={"Y-m-d H:i:s"}
                  name={"custom_data.print.date_format"}
                  tDefault={"Date format"}
                  tKey={"vestigingen.form.algemeen.label.date_format"}
                />
                <FormTextTC
                  defaultValue={"300"}
                  name={"custom_data.print.logo_size"}
                  tDefault={"Logo size"}
                  tKey={"vestigingen.form.algemeen.label.logo_size"}
                />
                <FormFieldTwoColumns>
                  <FormLabel>
                    <TranslationWrapper
                      tDefault={"Usable placeholder variables in text: "}
                      tKey={"vestigingen.form.berichten.label.orderMsg.placeholders"}
                    />
                    <div>
                      [id] , [oCnt] , [oCntShort] , [pickupNr] , [slotName] , [externOrderId] , [externTransId] ,
                      [tafelNr]
                    </div>
                  </FormLabel>
                </FormFieldTwoColumns>
                <FormTextTC
                  name={"custom_data.print.custom_qr"}
                  tDefault={"Add custom QR code to ticket"}
                  tKey={"vestigingen.form.algemeen.label.custom_qr"}
                />
                <FormTextTC
                  name={"custom_data.print.custom_text_below_qr"}
                  rows={5}
                  tDefault={"Add Text below QR code to ticket"}
                  tKey={"vestigingen.form.algemeen.label.custom_text_below_qr"}
                />
              </GridLegacy>
              <FormSwitchTC
                defaultValue={false}
                name={"custom_data.print.force_print_table"}
                tDefault={"Force print table"}
                tKey={"vestigingen.form.algemeen.label.force_print_table"}
              />
              <FormSwitchTC
                defaultValue={false}
                name={"custom_data.pos.send_pager_as_ordernumber"}
                tKey={"Send pagernumber as order number"}
              />
              <FormSwitchTC
                defaultValue={false}
                name={"custom_data.print.add_payment_log_to_ticket"}
                tKey={"Add paymentlog to ticket"}
              />
              <FormTextTC
                name={"custom_data.print.vestiging_print_name"}
                tDefault={"Print salesarea name"}
                tKey={"vestigingen.form.algemeen.label.vestiging_print_name"}
              />
              <FormSwitchTC
                defaultValue={false}
                name={"custom_data.print.use_extern_order_id_as_ocnt"}
                tKey={"Use external order id as oCnt"}
              />
              <FormSwitchTC
                defaultValue={false}
                name={"custom_data.print.wait_on_order_for_print_dispatch"}
                tKey={"Wait on API status for print dispatch"}
              />
            </CardBody>
          </Card>
        </GridLegacy>
      </GridLegacy>
    </div>
  );
}

export default Printers;
